/**
 * Single source of truth for PostHog configuration. Nothing else in the module
 * reads `process.env` — env access lives here so the rest of the code can be
 * reasoned about (and later moved to a shared package) without hunting for globals.
 */
import type { CaptureResult, PostHogConfig, Properties } from 'posthog-js';
import { isStageDev, isStageProd } from '@/lib/utils/env';

const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '';
const HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? '';
const ASSET_HOST = process.env.NEXT_PUBLIC_POSTHOG_ASSET_HOST ?? '';
const UI_HOST = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ?? '';
const INGEST_PATH = process.env.NEXT_PUBLIC_POSTHOG_INGEST_PATH ?? '';

/**
 * PostHog runs on the `prod` and `dev` stages. `NEXT_PUBLIC_STAGE` is set per
 * environment from Vault (`prod` on outpoll.com, `dev` on outpoll.io), so any
 * other stage never loads the SDK even when the project keys are present.
 * Which PostHog project each stage reports to is decided by the keys in that
 * environment's Vault mount. Local builds carry `dev` in `.env`, so PostHog can
 * be exercised locally by adding the keys there.
 */
const STAGE_ENABLED = isStageProd() || isStageDev();

/** Trim a trailing slash so `${base}${path}` never doubles up. */
function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

/**
 * PostHog is off outside the `prod`/`dev` stages, and off unless a project key
 * is configured. When off, `posthog-js` is never imported, so it stays out of the
 * runtime entirely — same contract as `isAnalyticsEnabled()` in
 * `lib/api/analytics/client.ts`.
 */
export function isPostHogEnabled(): boolean {
  return STAGE_ENABLED && KEY.length > 0 && getApiHost().length > 0;
}

export function getPostHogKey(): string {
  return KEY;
}

/**
 * The ingest path is only usable when the rewrites in `next.config.ts` actually
 * exist, and those require an enabled stage plus all three values. This
 * condition must stay in step with `POSTHOG_PROXY_ENABLED` there — a config
 * file can't import from `src/`, so the check is stated twice rather than shared.
 */
const PROXIED = STAGE_ENABLED && Boolean(INGEST_PATH && HOST && ASSET_HOST);

/**
 * Where the browser sends events. Prefers the same-origin ingest path — a
 * first-party origin survives tracking blockers and keeps the cookie
 * first-party. Falls back to talking to PostHog directly.
 */
export function getApiHost(): string {
  return stripTrailingSlash(PROXIED ? INGEST_PATH : HOST);
}

export function buildInitOptions(): Partial<PostHogConfig> {
  return {
    api_host: getApiHost(),

    // With a proxy, PostHog can no longer infer the dashboard URL from `api_host`,
    // so in-app links (toolbar, "view in PostHog") would point at our own origin.
    ...(PROXIED && UI_HOST ? { ui_host: stripTrailingSlash(UI_HOST) } : {}),

    // Only relevant when NOT proxying: the rewrites already route `/static/*`
    // and `/array/*` to the asset host, so the SDK needs no separate origin.
    ...(!PROXIED && ASSET_HOST ? { asset_host: stripTrailingSlash(ASSET_HOST) } : {}),

    // App Router client navigation never reloads the page, so automatic pageviews
    // would only ever record the first screen. `PostHogPageview` does it instead.
    capture_pageview: false,
    capture_pageleave: true,

    // The trading UI is click-heavy — autocapture would be noise and billing.
    // Events are declared explicitly in `events.ts`.
    autocapture: false,

    session_recording: {
      // KYC forms, passwords and amounts all live in inputs — mask them all.
      // Text is deliberately NOT masked wholesale: `maskTextSelector: '*'`
      // strips most of the diagnostic value out of a replay. Sensitive blocks
      // opt out individually with `class="ph-no-capture"`.
      maskAllInputs: true,
    },

    before_send: redactKycMobileSession,
  };
}

// The KYC phone-handoff id in `/kyc-mobile/{id}` lets anyone upload a selfie into that session
// without logging in, so it must never reach analytics.
const KYC_MOBILE_SESSION = /(\/kyc-mobile\/)[^/?#\s"]+/g;

function redactValue(value: unknown): unknown {
  return typeof value === 'string' ? value.replace(KYC_MOBILE_SESSION, '$1:id') : value;
}

function redactProps(props: Properties | undefined): Properties | undefined {
  if (!props) return props;
  return Object.fromEntries(Object.entries(props).map(([k, v]) => [k, redactValue(v)]));
}

function redactKycMobileSession(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  // Replay snapshots carry the URL deep inside the DOM payload — skip recording that page.
  if (event.event === '$snapshot' && window.location.pathname.includes('/kyc-mobile/')) {
    return null;
  }
  return {
    ...event,
    properties: redactProps(event.properties) ?? {},
    $set: redactProps(event.$set),
    $set_once: redactProps(event.$set_once),
  };
}
